import { defineStyle } from '@chakra-ui/react';

import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  BORDER_RADII,
  BORDER_STYLES,
  DISABLED_STYLES,
  FONT_SIZES,
  FONT_WEIGHTS,
  SPACING,
  TEXT_COLORS,
  TEXT_STYLES,
} from './tokens';
import { createResolveColor } from './utils';

/**
 * Text style patterns, used all over the place. Using this is preferable to using `TEXT_STYLES` and
 * `FONT_SIZES` directly because it allows us to modify typography across the app in one place.
 */
export const TypographyPattern = {
  variants: {
    'page-title': defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.black.base),
        fontSize: TEXT_STYLES['4xl'].fontSize,
        lineHeight: TEXT_STYLES['4xl'].lineHeight,
        fontWeight: FONT_WEIGHTS.bold,
      };
    }),

    'section-title': defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.black.base),
        fontSize: TEXT_STYLES['2xl'].fontSize,
        lineHeight: TEXT_STYLES['2xl'].lineHeight,
        fontWeight: FONT_WEIGHTS.semibold,
      };
    }),

    'section-subtitle': defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.black.base),
        fontSize: TEXT_STYLES.xl.fontSize,
        lineHeight: TEXT_STYLES.xl.lineHeight,
        fontWeight: FONT_WEIGHTS.semibold,
      };
    }),

    base: defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.prominent.base),
        fontSize: TEXT_STYLES.md.fontSize,
        lineHeight: TEXT_STYLES.md.lineHeight,
        fontWeight: FONT_WEIGHTS.normal,
      };
    }),

    'base-muted': defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.muted.base),
        fontSize: TEXT_STYLES.md.fontSize,
        lineHeight: TEXT_STYLES.md.lineHeight,
        fontWeight: FONT_WEIGHTS.normal,
      };
    }),

    detail: defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.prominent.base),
        fontSize: TEXT_STYLES.sm.fontSize,
        lineHeight: TEXT_STYLES.sm.lineHeight,
        fontWeight: FONT_WEIGHTS.normal,
      };
    }),

    'detail-muted': defineStyle((props) => {
      const resolveColor = createResolveColor(props);
      return {
        color: resolveColor(TEXT_COLORS.muted.base),
        fontSize: TEXT_STYLES.sm.fontSize,
        lineHeight: TEXT_STYLES.sm.lineHeight,
        fontWeight: FONT_WEIGHTS.normal,
      };
    }),

    'button-text': defineStyle({
      fontWeight: FONT_WEIGHTS.semibold,
      userSelect: 'none',
    }),

    'table-header': defineStyle({
      fontStyle: 'normal',
      fontSize: TEXT_STYLES.sm.fontSize,
      fontWeight: FONT_WEIGHTS.bold,
      lineHeight: FONT_SIZES.xl,
      textTransform: 'capitalize',
      letterSpacing: 0,
      userSelect: 'none',
    }),
  },
};

/**
 * This is a poor name for a core design system style pattern that is... hard to pin down. Roughly,
 * there is a group of related elements that take up "button-sized" space, and share a lot of visual
 * elements with buttons. For example, some form inputs, alerts, and tags, can look like some
 * variants of buttons (e.g. text inputs look like outlined buttons).
 *
 * This pattern has the following common traits:
 *
 * - common variants:
 *    - solid, solid-muted: full-color background
 *    - outline, outline-muted, ghost: transparent background, unless hovered
 *    - link: underlined text, no padding
 * - common interactive (hover/active/disabled) styles
 * - common sizes
 * - common typography
 *
 * Some examples of elements that use this pattern:
 *
 * - buttons: regular buttons, but also dropdown/select/date-picker/etc. triggers
 * - tags: look like buttons (background), but have fully-rounded corners, and aren't clickable
 * - alerts: look like `solid-muted` buttons (background, border radius)
 * - stepper: the "step" looks like `solid`, `solid-muted`, or `outline` buttons, depending on state
 * - (TODO) cards: look like `outline-muted` buttons, but not typically clickable
 * - (TODO) form inputs:
 *    - text inputs look like `outline`, `outline-muted` different typography
 *    - checkboxes and radio buttons look like `solid` and `outline` buttons
 *    - dropdown/select triggers are actual `outline` and `outline-muted` buttons under the hood
 */
export const ButtonLikePattern = {
  baseStyles: {
    static: defineStyle({
      ...TypographyPattern.variants['button-text'],
      boxShadow: 'none',
    }),
    interactive: defineStyle({
      cursor: 'pointer',
      opacity: 1.0,
      _disabled: {
        cursor: 'not-allowed',
        opacity: DISABLED_STYLES.opacity,
      },
    }),
  },

  sizes: {
    layout: {
      // <Alert size="sm" >
      // <Button size="sm" >
      // <Tag size="sm" >
      sm: defineStyle({
        fontSize: TEXT_STYLES.sm.fontSize,
        lineHeight: TEXT_STYLES.sm.lineHeight,
        paddingBlock: SPACING[2],
        paddingInline: SPACING[3],
        borderRadius: BORDER_RADII.base,
      }),

      // <Alert size="md" >
      // <Button size="md" >
      // <Tag size="md" >
      md: defineStyle({
        fontSize: TEXT_STYLES.md.fontSize,
        lineHeight: TEXT_STYLES.md.lineHeight,
        paddingBlock: SPACING[3],
        paddingInline: SPACING[4],
        borderRadius: BORDER_RADII.md,
      }),

      // <Alert size="lg" >
      // <Button size="lg" >
      // <Tag size="lg" >
      lg: defineStyle({
        fontSize: TEXT_STYLES.lg.fontSize,
        lineHeight: TEXT_STYLES.lg.lineHeight,
        paddingBlock: SPACING[4],
        paddingInline: SPACING[5],
        borderRadius: BORDER_RADII.lg,
      }),
    },

    fixedHeight: {
      // <Button size="sm" >
      // <Tag size="sm" >
      sm: defineStyle({ height: '2rem' }),

      // <Button size="md" >
      // <Tag size="md" >
      md: defineStyle({ height: '2.5rem' }),

      // <Button size="lg" >
      // <Tag size="lg" >
      lg: defineStyle({ height: '3rem' }),
    },
  },

  variants: {
    // <Button variant="primary">
    // <Stepper>'s "step" subcomponent
    /** Prominent, solid background color, white text, no distinctive border */
    solid: {
      static: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          opacity: 1.0,
          color: resolveColor(TEXT_COLORS.white.base),
          bgColor: resolveColor(BACKGROUND_COLORS.prominent.base),
          borderWidth: BORDER_STYLES.thick.borderWidth,
          borderStyle: BORDER_STYLES.thick.borderStyle,
          borderColor: resolveColor(BORDER_COLORS.prominent.base),
        };
      }),

      interactive: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          _hover: {
            color: resolveColor(TEXT_COLORS.white.base),
            bgColor: resolveColor(BACKGROUND_COLORS.prominent.hover),
            borderColor: resolveColor(BORDER_COLORS.prominent.hover),
            _disabled: {
              bgColor: resolveColor(BACKGROUND_COLORS.prominent.base),
              borderColor: resolveColor(BORDER_COLORS.prominent.base),
            },
          },
          _active: {
            color: resolveColor(TEXT_COLORS.white.base),
            bgColor: resolveColor(BACKGROUND_COLORS.prominent.active),
            borderColor: resolveColor(BORDER_COLORS.prominent.active),
          },
          _disabled: {
            color: resolveColor(TEXT_COLORS.white.base),
            bgColor: resolveColor(BACKGROUND_COLORS.prominent.base),
            borderColor: resolveColor(BORDER_COLORS.prominent.base),
          },
        };
      }),
    },

    // <Alert variant="subtle">
    // <Button variant="primary-muted">
    // <Tag variant="solid-muted">
    /** Muted, solid background color, white text, no distinctive border */
    'solid-muted': {
      static: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          color: resolveColor(TEXT_COLORS.muted.base),
          bgColor: resolveColor(BACKGROUND_COLORS.muted.base),
          borderWidth: BORDER_STYLES.thick.borderWidth,
          borderStyle: BORDER_STYLES.thick.borderStyle,
          borderColor: resolveColor(BORDER_COLORS.muted.base),
        };
      }),

      interactive: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          _hover: {
            color: resolveColor(TEXT_COLORS.muted.hover),
            bgColor: resolveColor(BACKGROUND_COLORS.muted.hover),
            borderColor: resolveColor(BORDER_COLORS.muted.hover),
            _disabled: {
              color: resolveColor(TEXT_COLORS.muted.disabled),
              bgColor: resolveColor(BACKGROUND_COLORS.muted.disabled),
              borderColor: resolveColor(BORDER_COLORS.muted.disabled),
            },
          },
          _active: {
            color: resolveColor(TEXT_COLORS.white.base),
            bgColor: resolveColor(BACKGROUND_COLORS.prominent.active),
            borderColor: resolveColor(BORDER_COLORS.prominent.active),
          },
          _disabled: {
            color: resolveColor(TEXT_COLORS.muted.disabled),
            bgColor: resolveColor(BACKGROUND_COLORS.muted.disabled),
            borderColor: resolveColor(BORDER_COLORS.muted.disabled),
          },
        };
      }),
    },

    // <Button variant="secondary">
    // <Stepper>'s "step" subcomponent
    /** Transparent background, colorful text, think distinctive border */
    outline: {
      static: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          color: resolveColor(TEXT_COLORS.muted.base),
          bgColor: resolveColor(BACKGROUND_COLORS.ghost.base),
          borderWidth: BORDER_STYLES.thick.borderWidth,
          borderStyle: BORDER_STYLES.thick.borderStyle,
          borderColor: resolveColor(BORDER_COLORS.prominent.base),
        };
      }),

      interactive: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          _hover: {
            color: resolveColor(TEXT_COLORS.muted.hover),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.hover),
            borderColor: resolveColor(BORDER_COLORS.prominent.hover),
            _disabled: {
              color: resolveColor(TEXT_COLORS.muted.disabled),
              bgColor: resolveColor(BACKGROUND_COLORS.ghost.disabled),
              borderColor: resolveColor(BORDER_COLORS.prominent.disabled),
            },
          },
          _active: {
            color: resolveColor(TEXT_COLORS.muted.active),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.active),
            borderColor: resolveColor(BORDER_COLORS.prominent.active),
          },
          _disabled: {
            color: resolveColor(TEXT_COLORS.muted.disabled),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.disabled),
            borderColor: resolveColor(BORDER_COLORS.prominent.disabled),
          },
        };
      }),
    },

    // <Button variant="secondary-muted">
    // <Tag variant="outline-muted">
    /** Transparent background, colorful text, thin distinctive border */
    'outline-muted': {
      static: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          color: resolveColor(TEXT_COLORS.muted.base),
          bgColor: resolveColor(BACKGROUND_COLORS.ghost.base),
          borderWidth: BORDER_STYLES.thin.borderWidth,
          borderStyle: BORDER_STYLES.thin.borderStyle,
          borderColor: resolveColor(BORDER_COLORS.bright.base),
        };
      }),

      interactive: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          _hover: {
            color: resolveColor(TEXT_COLORS.muted.hover),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.hover),
            borderColor: resolveColor(BORDER_COLORS.bright.hover),
            _disabled: {
              color: resolveColor(TEXT_COLORS.muted.disabled),
              bgColor: resolveColor(BACKGROUND_COLORS.ghost.disabled),
              borderColor: resolveColor(BORDER_COLORS.bright.disabled),
            },
          },
          _active: {
            color: resolveColor(TEXT_COLORS.muted.active),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.active),
            borderColor: resolveColor(BORDER_COLORS.bright.active),
          },
          _disabled: {
            color: resolveColor(TEXT_COLORS.muted.disabled),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.disabled),
            borderColor: resolveColor(BORDER_COLORS.bright.disabled),
          },
        };
      }),
    },

    // <Button variant="v2-ghost">
    // <Tag variant="ghost">
    /** Transparent background, colorful text, no distinctive border */
    ghost: {
      static: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          color: resolveColor(TEXT_COLORS.muted.base),
          bgColor: resolveColor(BACKGROUND_COLORS.ghost.base),
          borderWidth: BORDER_STYLES.thin.borderWidth,
          borderStyle: BORDER_STYLES.thin.borderStyle,
          borderColor: resolveColor(BORDER_COLORS.transparent.base),
        };
      }),

      interactive: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          _hover: {
            color: resolveColor(TEXT_COLORS.muted.hover),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.hover),
            borderColor: resolveColor(BORDER_COLORS.transparent.hover),
            _disabled: {
              color: resolveColor(TEXT_COLORS.muted.disabled),
              bgColor: resolveColor(BACKGROUND_COLORS.ghost.disabled),
              borderColor: resolveColor(BORDER_COLORS.transparent.disabled),
            },
          },
          _active: {
            color: resolveColor(TEXT_COLORS.muted.active),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.active),
            borderColor: resolveColor(BORDER_COLORS.transparent.active),
          },
          _disabled: {
            color: resolveColor(TEXT_COLORS.muted.disabled),
            bgColor: resolveColor(BACKGROUND_COLORS.ghost.disabled),
            borderColor: resolveColor(BORDER_COLORS.transparent.disabled),
          },
        };
      }),
    },

    // <Button variant="link">
    /** Transparent background, colorful text, no distinctive border, underline, no paddingf */
    link: {
      interactive: defineStyle((props) => {
        const resolveColor = createResolveColor(props);
        return {
          textDecoration: 'underline',
          _hover: {
            textDecoration: 'underline',
            color: resolveColor(TEXT_COLORS.muted.hover),
            _disabled: {
              textDecoration: 'underline',
              color: resolveColor(TEXT_COLORS.muted.disabled),
            },
          },
          _active: {
            textDecoration: 'underline',
            color: resolveColor(TEXT_COLORS.muted.active),
          },
          _disabled: {
            textDecoration: 'underline',
            color: resolveColor(TEXT_COLORS.muted.disabled),
          },
        };
      }),
    },
  },
};
